import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Select, { MultiValue, SingleValue, StylesConfig } from 'react-select';
import { ICellEditor, ICellEditorParams } from 'ag-grid-community';
import cn from 'classnames';


import DropdownIndicator from '@/components/common/Dropdown/components/DropdownIndicator';
import { useOnClickOutside } from '@/hooks/useOnClickOutside.hook';
import focusStyles from './dropdown-grid-styles.module.scss';
import { CONTRACT_LINES_AMOUNT_WITH_EXTRA_SPACE_BELOW } from '@/config/common.config';
import { notificationService } from '@/services/notification/notification.client';
import { GetPaginatedFilteredPurchaseOrdersVariables } from
  '@/graphql/contracts/_generated_/getPaginatedFilteredPurchaseOrders.query';
// eslint-disable-next-line max-len
import AddPurchaseOrderModal from '@/pages/ContractDetails/RightPanel/Billing/AssignPurchaseOrderForLines/components/AddPurchaseOrderModal/AddPurchaseOrderModal';

export type InputSearchParams = {
  searchString: string;
  searchResults?: number;
  customerId?: string;
  customerName?: string;
  customerCode?: string;
  currency?: string;
};

export interface IAgGridPurchaseOrderDropdownEditorAsyncProps<Option> extends ICellEditorParams {
  value: Option;
  placeholder?: string;
  getOptionLabel: (option: Option) => string;
  getOptionValue: (option: Option) => any;
  handleInputChange: (params: GetPaginatedFilteredPurchaseOrdersVariables) => Promise<Option[]>;
  isDisabled: boolean;
  saveOnChange?: boolean;
  autofocus?: boolean;
  styles?: StylesConfig<Option>;
  searchResults?: number;
  isClearable?: boolean;
  customerId?: string;
  customerName?: string;
  customerCode?: string;
  currency?: string;
}

const FIRST_EDITABLE_FIELD = 0;

function AgGridPurchaseOrderDropdownEditorAsync<Option>(
  props: IAgGridPurchaseOrderDropdownEditorAsyncProps<Option>,
  ref: ForwardedRef<ICellEditor>
) {
  const {
    value,
    placeholder,
    getOptionLabel,
    getOptionValue,
    handleInputChange,
    isDisabled,
    api,
    saveOnChange,
    autofocus,
    styles,
    rowIndex,
    colDef,
    customerId,
    customerName,
    customerCode,
    currency
  } = props;


  const refDropdown = useRef<any>(null);
  const refContainer = useRef<HTMLDivElement | null>(null);

  const [optionsList, setOptionsList] = useState<Option[]>([]);
  const [currentOption, setCurrentOption] = useState<SingleValue<Option> | MultiValue<Option> | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [typedPoNumber, setTypedPoNumber] = useState('');
  const isLastRow = api.getEditingCells()[FIRST_EDITABLE_FIELD]?.rowIndex === api.getLastDisplayedRowIndex()
    && api.getRenderedNodes().length > CONTRACT_LINES_AMOUNT_WITH_EXTRA_SPACE_BELOW;

  const defaultPlaceholder = useMemo(() => {
    return value ? getOptionLabel(value) : 'Select';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useOnClickOutside(refContainer, () => {
    setIsDropdownOpen(false);
  });

  const handleCustomOptionSelection = (selectedOption: any) => {
    setTypedPoNumber(selectedOption.typedValue);
    setIsModalOpen(true);
    setIsDropdownOpen(false);
  };

  const onChange = (selectedOption: SingleValue<Option> | MultiValue<Option>, { action }: any) => {
    if (action === 'clear') {
      setCurrentOption({ __cleared: true } as any);
      setIsDropdownOpen(false);
      setIsEditing(false);
      return;
    }

    if (selectedOption && (selectedOption as any).isCustomOption) {
      handleCustomOptionSelection(selectedOption);
      return;
    }

    setCurrentOption(selectedOption ?? null);
    setIsDropdownOpen(false);

    if (saveOnChange) {
      setIsEditing(false);
    }
  };

  const shouldPreventDropdownInteraction = () => isModalOpen;

  const loadPurchaseOrders = () => {
    if (typeof handleInputChange === 'function') {
      setIsLoading(true);
      handleInputChange({
        filter: {
          customerCode: { eq: customerCode },
          poCurrency: { eq: currency }
        }
      })
        .then((result: Option[]) => setOptionsList(result))
        .catch((err) => notificationService.open({
          title: 'Error',
          text: err?.message || 'Something went wrong',
          type: 'danger'
        }))
        .finally(() => setIsLoading(false));
    }
  };

  const onOpen = () => {
    if (shouldPreventDropdownInteraction()) return;

    setIsDropdownOpen(true);
    loadPurchaseOrders();
  };

  const createNewPoOption = (searchString: string): Option => ({
    id: 'create-new-po',
    poNumber: `Create new PO: ${searchString}`,
    isCustomOption: true,
    typedValue: searchString
  } as Option);

  const onInputChange = (searchString: string) => {
    if (shouldPreventDropdownInteraction()) return;
    if (searchString.length > 0) {
      setIsLoading(true);
      handleInputChange({
        filter: {
          customerCode: { eq: customerCode },
          poCurrency: { eq: currency }
        }
      })
        .then((result: Option[]) => {
          const filteredOptions = result.filter((option) =>
            getOptionLabel(option).toLowerCase().includes(searchString.toLowerCase())
          );
          const newPoOption = createNewPoOption(searchString);
          setOptionsList([...filteredOptions, newPoOption]);
          setTypedPoNumber(searchString);
        })
        .catch((err) => notificationService.open({
          title: 'Error',
          text: err?.message || 'Something went wrong',
          type: 'danger'
        }))
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      onOpen();
    }
  };

  useImperativeHandle(ref, () => ({
    getValue() {
      return currentOption || null;
    },

    focusIn() {
      refDropdown.current?.focus();
    },

    isPopup() {
      return false;
    },
  }));

  useEffect(() => {
    // focusIn doesn't work on mount for some reasons
    // so this is way to focus dropdown on mount
    const focusCellPosition = api.getFocusedCell();
    const isRowFocused = focusCellPosition
      && focusCellPosition.rowIndex === rowIndex
      && focusCellPosition.column.getColId() === colDef.colId;

    if (autofocus || isRowFocused) {
      // setTimeout to call focus asynchronously after all elements are ready
      setTimeout(() => {
        refDropdown.current?.focus();
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (!isEditing && saveOnChange) {
      api.stopEditing();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isEditing]);

  useEffect(() => {
    if (optionsList.length) {
      setIsDropdownOpen(true);
    }
  }, [optionsList]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTypedPoNumber('');
    setIsEditing(false);
  };

  const handleModalSuccess = (createdPurchaseOrder: any) => {
    setCurrentOption(createdPurchaseOrder);
    setIsModalOpen(false);
    setTypedPoNumber('');
    setIsEditing(false);
  };

  return (
    <>
      <div ref={refContainer}>
        <Select
          ref={refDropdown}
          options={optionsList}
          className={cn('ag-dropdown', focusStyles['custom-focus'])}
          classNamePrefix="ag-dropdown"
          components={{ DropdownIndicator }}
          isLoading={isLoading}
          placeholder={placeholder ?? defaultPlaceholder}
          onChange={(option, actionMeta) => onChange(option, actionMeta)}
          onInputChange={(inputValue, actionMeta) => {
            if (actionMeta.action === 'input-change') {
              onInputChange(inputValue);
            }
          }} getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          isSearchable={true}
          isDisabled={isDisabled}
          onMenuOpen={onOpen}
          menuIsOpen={isDropdownOpen}
          menuPortalTarget={document.querySelector('body')}
          filterOption={() => true}
          styles={styles}
          menuPlacement={isLastRow ? 'top' : 'auto'}
          isClearable={true}
        />
      </div>
      <AddPurchaseOrderModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        initialPoNumber={typedPoNumber}
        customerId={customerId}
        customerName={customerName}
        customerCode={customerCode}
        currency={currency}
      />
    </>
  );
}

export default forwardRef(AgGridPurchaseOrderDropdownEditorAsync);
